import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, CheckCircle2, FileText, Image as ImageIcon, X, Film, XCircle } from 'lucide-react';
import { format } from 'date-fns';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const MAX_MB = 20;
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,video/mp4,video/webm,video/quicktime';

export function ParceiroPublicoPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [queue, setQueue] = useState([]); // [{id, file, progress, done, error, result}]
  const fileRef = useRef(null);
  const nextId = useRef(1);

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/public/parceiros/${token}`);
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || 'Erro');
      setData(j);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const uploadFile = (item) => new Promise((resolve) => {
    const form = new FormData();
    form.append('file', item.file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/public/parceiros/${token}/midias`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, progress: pct } : x));
      }
    };
    xhr.onload = () => {
      let j; try { j = JSON.parse(xhr.responseText); } catch { j = {}; }
      if (xhr.status >= 200 && xhr.status < 300) {
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, done: true, progress: 100, result: j.midia } : x));
      } else {
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, error: j?.error || `HTTP ${xhr.status}` } : x));
      }
      resolve();
    };
    xhr.onerror = () => {
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, error: 'Falha de rede' } : x));
      resolve();
    };
    xhr.send(form);
  });

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const itens = files.filter((f) => f.size <= MAX_MB * 1024 * 1024).map((f) => ({
      id: nextId.current++,
      file: f,
      progress: 0,
      done: false,
      error: null,
      result: null,
    }));
    setQueue((q) => [...q, ...itens]);
    for (const it of itens) {
      // eslint-disable-next-line no-await-in-loop
      await uploadFile(it);
    }
    await load();
    if (fileRef.current) fileRef.current.value = '';
  };

  if (loading) return <div className="pub-bg"><div className="pub-card center" style={{ minHeight: 200 }}><span className="spinner" /></div></div>;
  if (err) return (
    <div className="pub-bg">
      <div className="pub-card">
        <XCircle size={48} color="var(--c-danger)" style={{ margin: '0 auto 12px', display: 'block' }} />
        <h1 className="pub-title center">Link inválido</h1>
        <p className="muted center">{err}</p>
      </div>
    </div>
  );

  const { parceiro, midias } = data;
  const totalEnviadas = midias.length;

  return (
    <div className="pub-bg">
      <div className="pub-card" style={{ maxWidth: 700 }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {parceiro.secretaria_nome} · Envio de mídias
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 4px' }}>{parceiro.nome}</h1>
          <div className="text-sm muted">Para a demanda: {parceiro.demanda_titulo}</div>
        </div>

        {parceiro.observacoes && (
          <div style={{ background: 'var(--c-surface-2)', padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 14, whiteSpace: 'pre-wrap' }}>
            {parceiro.observacoes}
          </div>
        )}

        <input ref={fileRef} type="file" accept={ACCEPT} multiple onChange={onPick} style={{ display: 'none' }} />

        <button
          onClick={() => fileRef.current?.click()}
          className="btn btn-secondary btn-block"
          style={{ padding: 26, flexDirection: 'column', border: '2px dashed var(--c-primary)', background: 'white', color: 'var(--c-primary)' }}
        >
          <Upload size={32} />
          <div style={{ marginTop: 8, fontSize: 15, fontWeight: 600 }}>Enviar fotos ou vídeos</div>
          <div className="text-xs muted" style={{ marginTop: 4 }}>PNG · JPG · MP4 · PDF · até {MAX_MB}MB cada</div>
        </button>

        {queue.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Enviando</div>
            <div className="stack" style={{ gap: 8 }}>
              {queue.map((it) => (
                <div key={it.id} style={{ padding: 10, border: '1px solid var(--c-border)', borderRadius: 8, background: 'white' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    {it.file.type.startsWith('image/') ? <ImageIcon size={16} /> : it.file.type.startsWith('video/') ? <Film size={16} /> : <FileText size={16} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="truncate" style={{ fontSize: 13, fontWeight: 500 }}>{it.file.name}</div>
                    </div>
                    {it.done && <CheckCircle2 size={18} color="var(--c-success)" />}
                    {it.error && <XCircle size={18} color="var(--c-danger)" />}
                  </div>
                  {!it.done && !it.error && (
                    <div style={{ height: 4, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${it.progress}%`, height: '100%', background: 'var(--c-primary)', transition: 'width 0.2s' }} />
                    </div>
                  )}
                  {it.error && <div style={{ fontSize: 12, color: 'var(--c-danger)' }}>{it.error}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {totalEnviadas > 0 && (
          <div style={{ marginTop: 22 }}>
            <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
              Já enviadas ({totalEnviadas})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 6 }}>
              {midias.map((m) => {
                const isImg = m.mime?.startsWith('image/');
                const isVideo = m.mime?.startsWith('video/');
                return (
                  <a key={m.id} href={m.url} target="_blank" rel="noreferrer" style={{ aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: '#000', display: 'block' }}>
                    {isImg ? <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                     isVideo ? <video src={m.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                     <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={24} color="#fff" /></div>}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs muted center" style={{ marginTop: 24, borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
          Secretaria de Comunicação · Prefeitura de Itapecerica-MG<br />
          As mídias serão recebidas automaticamente pela equipe.
        </div>
      </div>
    </div>
  );
}
